import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Container,
  TextField,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { MusicNote as MusicNoteIcon } from '@mui/icons-material';
import { useAuth } from '../context';
import { useLocation } from 'react-router-dom';
import { firebaseService } from '../services/firebase';

const Login: React.FC = () => {
  const { login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [firebaseAuthenticated, setFirebaseAuthenticated] = useState(false);
  
  // Handle Firebase login
  const handleFirebaseLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    try {
      const success = await firebaseService.login(email, password);
      if (success) {
        setFirebaseAuthenticated(true);
        setError(null);
      } else {
        setError('Login failed. Please check your credentials and try again.');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during login');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle Spotify login (only available after Firebase authentication)
  const handleSpotifyLogin = () => {
    login();
    // The redirect to callback URL is handled by the Spotify auth flow
  };
  
  // Handle signup redirect
  const handleSignup = () => {
    firebaseService.redirectToSignup();
  };
  
  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            borderRadius: 2
          }}
        >
            <Box 
            sx={{ 
              mb: 3, 
              width: 100, 
              height: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            >
            <Box
              component="img"
              src="/assets/otto_logo.png"
              alt="Otto Logo"
              sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
              }}
            />
            </Box>
          
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Tunes with Otto
          </Typography>
          
          <Typography variant="subtitle1" color="text.secondary" align="center" sx={{ mb: 4 }}>
            Your AI-powered music companion
          </Typography>
          
          {!firebaseAuthenticated ? (
            <>
              <Typography variant="h6" component="h2" gutterBottom>
                Log in to Green Tokens
              </Typography>
              
              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              <Box component="form" onSubmit={handleFirebaseLogin} sx={{ width: '100%', mt: 1 }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Log In'}
                </Button>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Typography variant="body2">
                    Don't have an account?{' '}
                    <Button 
                      color="primary" 
                      onClick={handleSignup}
                      sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                    >
                      Sign up
                    </Button>
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
                Green Tokens authentication is required to access the AI features.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body1" align="center" sx={{ mb: 4 }}>
                You're logged in to Green Tokens! Now connect with your Spotify account to get personalized music recommendations, 
                create playlists, and chat with Otto about all things music.
              </Typography>
              
              <Button 
                variant="contained" 
                size="large" 
                onClick={handleSpotifyLogin}
                sx={{ 
                  py: 1.5, 
                  px: 4, 
                  borderRadius: 8,
                  bgcolor: '#1DB954', // Spotify green
                  '&:hover': {
                    bgcolor: '#1AA34A',
                  }
                }}
              >
                Connect with Spotify
              </Button>
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
                By connecting, you agree to our Terms of Service and Privacy Policy.
                We only request access to your Spotify data to provide personalized recommendations.
              </Typography>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
